import React, { useMemo } from 'react';

import { generateEntryKey } from '../../../../utils/nameMatching';
import Table from '../../../common/Table/Table.module';
import type { NotInRnicDecision } from '../ImportDetail.module';

interface TypeCheckboxStepProps {
  entries: NotInRnicDecision[];
  fileAddressMap: Map<string, string>;
  onSelectType: (entryKey: string, forcedType: number) => void;
  onIgnore: (entryKey: string) => void;
}

const TYPE_COLUMNS = [
  { key: 'ASL', value: 2 },
  { key: 'AFUL', value: 4 },
  { key: 'Tertiaire', value: 3 },
  { key: 'Copro', value: 1 },
] as const;

const TypeCheckboxStep: React.FC<TypeCheckboxStepProps> = ({ entries, fileAddressMap, onSelectType, onIgnore }) => {
  const tableData = useMemo(() => {
    return entries.map((entry) => {
      const entryKey = generateEntryKey(entry.immatriculation, entry.name);
      const address = fileAddressMap.get(entryKey) || '';
      const isForced = entry.decision === 'force' && entry.forcedType !== undefined;
      const isSkipped = entry.decision === 'skip';

      const baseRow: Record<string, React.ReactNode> = {
        id: entryKey,
        Immat: entry.correctedImmatriculation || entry.immatriculation || '-',
        NOM: entry.name,
        'ADRESSE FICHIER': address || '-',
        Statut: (
          <span
            className={`rounded px-2 py-1 text-[11px] font-semibold ${
              isSkipped
                ? 'bg-red-100 text-red-700'
                : isForced
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {isSkipped ? 'Ignoré' : isForced ? 'Forcé' : 'En attente'}
          </span>
        ),
      };

      TYPE_COLUMNS.forEach((col) => {
        const checked = isForced && entry.forcedType === col.value;
        baseRow[col.key] = (
          <div className="flex justify-center">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onSelectType(entryKey, col.value)}
              disabled={isSkipped}
              className={`h-4 w-4 rounded border-gray-300 text-rgpd-blue-dark focus:ring-rgpd-blue-dark ${
                isSkipped ? 'cursor-not-allowed opacity-50' : ''
              }`}
            />
          </div>
        );
      });

      baseRow.Ignorer = (
        <div className="flex justify-center">
          <button
            onClick={() => onIgnore(entryKey)}
            className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
              isSkipped
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            {isSkipped ? 'Ignoré' : 'Ignorer'}
          </button>
        </div>
      );

      return baseRow;
    });
  }, [entries, fileAddressMap, onSelectType, onIgnore]);

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-blue-900">Cocher ASL/AFUL/Tertiaire/Copro</h3>
          <p className="text-xs text-blue-800">
            Entrées sans numéro d&apos;immatriculation. Choisissez un type (une seule sélection par ligne).
          </p>
        </div>
        <span className="text-sm font-medium text-blue-900">{entries.length} élément(s)</span>
      </div>

      {entries.length > 0 ? (
        <>
          <Table
            data={tableData}
            headers={['Immat', 'NOM', 'ADRESSE FICHIER', 'Statut', 'ASL', 'AFUL', 'Tertiaire', 'Copro', 'Ignorer']}
            idKey="id"
            tableClass="text-xs"
            autoresize
            stickyHeader
            noPaddingColumns={['ASL', 'AFUL', 'Tertiaire', 'Copro', 'Ignorer']}
            tbodyClass="align-middle"
          />

          <div className="mt-2 text-[11px] text-blue-800">
            - Sélectionner une case force le type correspondant.
            <br />
            - Recliquer sur la même case retire la sélection (revient en « pending »).
            <br />
            - Une seule case peut être active par ligne.
          </div>
        </>
      ) : (
        <div className="rounded-md border border-dashed border-blue-200 bg-white px-4 py-6 text-center text-sm text-blue-800">
          Aucune entrée sans immatriculation à typer.
        </div>
      )}
    </div>
  );
};

export default TypeCheckboxStep;
